import { extractPagyHeadersFromFetch } from '~/utils/pagy';
import type { User, UserV1, UsersCount, UsersSearchParams, ApptegyAccount } from '@@/types/Users';
import type { UsersRepository, BulkAction, PaginationData } from '@@/types/UsersRepository';
import type { FusionResyncResponse, FusionSyncStatus } from '@@/types/FusionSync';
import type { IGeneralInfo, JSONApiUser } from '../serializers/users';
import { SerializeQueryParams, SerializeUserPayload, serializeApptegyAccountPayload } from '../serializers/users';
import { deserializeJSONApi, JSONApiHeaders } from '../serializers/jsonapi.js';
import type { JSONApiResponse } from '../serializers/jsonapi';
import { useAuthServiceApi, useAuthServiceApiV2, useRawAuthServiceApiV2 } from '~/composables/useAuthServiceApi';

export default function Users(): UsersRepository {
  // TODO: check if headers object is needed
  const headers = { APPTEGY_SERVICE: 'user_management' };
  return {
    async get(userID: string): Promise<UserV1> {
      const result = await useAuthServiceApi<JSONApiResponse<JSONApiUser>>(`/users/${userID}?include=apptegy-accounts`);
      //TODO: improve this work around to prevent camelcase of settings in apptegy accounts
      const deserialized: object = await deserializeJSONApi(result, { keyForAttribute: (s) => s });
      const user = camelCaseKeys<UserV1>(deserialized);
      user.apptegyAccounts = user.apptegyAccounts.map<ApptegyAccount>(camelCaseKeys);
      return user;
    },
    async getFusionSync(clientId: string | number, userId: string | number): Promise<FusionSyncStatus> {
      return useAuthServiceApiV2<FusionSyncStatus>(`clients/${clientId}/users/${userId}/fusion_sync`);
    },
    async postFusionResync(clientId: string | number, userId: string | number): Promise<FusionResyncResponse> {
      return useAuthServiceApiV2<FusionResyncResponse>(`clients/${clientId}/users/${userId}/fusion_resync`, {
        method: 'POST',
      });
    },
    async search(client: string, payload: UsersSearchParams): Promise<[User[], PaginationData]> {
      const params = SerializeQueryParams(payload);

      const response = await extractPagyHeadersFromFetch(
        useRawAuthServiceApiV2<User[]>(`/clients/${client}/users?${params}`),
      );
      response[0] = deepCamelCaseKeys(response[0]);
      return response;
    },
    async count(client: string): Promise<UsersCount> {
      return await useAuthServiceApiV2<UsersCount>(`clients/${client}/users/counts`);
    },
    async resetPassword(login: string) {
      return await useAuthServiceApi('/users/reset_password', { method: 'POST', body: { login } });
    },
    async bulkActions(client: string, data: BulkAction, _query: UsersSearchParams) {
      const query = SerializeQueryParams(_query);
      return await useAuthServiceApiV2(`clients/${client}/users/bulk_actions?${query}`, {
        method: 'PATCH',
        body: data,
      });
    },
    async fetchCsv(client, data) {
      return await useAuthServiceApiV2(`clients/${client}/users/template.csv`, {
        method: 'POST',
        body: data,
        headers,
      });
    },
    async create(user: IGeneralInfo, clientId?: string | number): Promise<User> {
      const body = SerializeUserPayload(user, clientId);
      const resp = await useAuthServiceApi('/users', { method: 'POST', body, headers: JSONApiHeaders });
      return deserializeJSONApi(resp);
    },
    async update(user: IGeneralInfo, userId: string | number, clientId?: string | number): Promise<User> {
      const body = SerializeUserPayload(user, clientId, userId);
      const resp = await useAuthServiceApi(`/users/${userId}`, { method: 'PATCH', body, headers: JSONApiHeaders });
      return deserializeJSONApi(resp);
    },
    async saveApptegyAccount(apptegyAccount: ApptegyAccount) {
      const url = `/apptegy-accounts/${apptegyAccount.id || ''}`;
      const method = apptegyAccount.id ? 'PATCH' : 'POST';
      const body = serializeApptegyAccountPayload(apptegyAccount);
      const resp = await useAuthServiceApi(url, { method, body, headers: JSONApiHeaders });
      return resp;
    },
    async deleteApptegyAccount(apptegyAccountID: string | number) {
      return await useAuthServiceApi(`/apptegy-accounts/${apptegyAccountID}`, { method: 'DELETE' });
    },
    importFiles: {
      find: async (client, id) =>
        useAuthServiceApiV2(`/clients/${client}/users/import_files/${id}`, {
          method: 'GET',
          headers,
        }),
      post: async (client, data) =>
        useAuthServiceApiV2(`/clients/${client}/users/import_files`, {
          method: 'POST',
          body: data,
          headers,
        }),
      put: async (client, id, data) =>
        useAuthServiceApiV2(`/clients/${client}/users/import_files/${id}`, {
          method: 'PUT',
          body: data,
          headers,
        }),
      getFailures: async (client, id, params) => {
        return useAuthServiceApiV2(`/clients/${client}/users/import_files/${id}/failures.json`, {
          method: 'GET',
          headers,
          params,
        });
      },
    },
  };
}
